import React, { forwardRef, useId } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Check,
  Info,
  LoaderCircle,
  LockKeyhole,
  RotateCcw,
  X,
  XCircle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { MOTION_TOKENS } from '../../lib/motionTokens';

const MotionDiv = motion.div;

const BUTTON_VARIANTS = {
  primary: 'linkit-control-primary',
  secondary: 'linkit-control-secondary',
  quiet: 'linkit-control-quiet',
  danger: 'linkit-control-danger',
  security: 'linkit-control-security',
};

const BUTTON_SIZES = {
  sm: 'linkit-control-sm',
  md: 'linkit-control-md',
  lg: 'linkit-control-lg',
};

export const ProductButton = forwardRef(function ProductButton(
  {
    children,
    className,
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingLabel = 'Working',
    icon,
    type = 'button',
    disabled,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'linkit-control',
        BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary,
        BUTTON_SIZES[size] || BUTTON_SIZES.md,
        className,
      )}
      {...props}
    >
      {loading ? <LoaderCircle size={16} className="animate-spin" aria-hidden="true" /> : icon}
      <span>{loading ? loadingLabel : children}</span>
    </button>
  );
});

export const ProductIconButton = forwardRef(function ProductIconButton(
  { children, className, label, variant = 'quiet', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn('linkit-icon-control', `linkit-icon-control-${variant}`, className)}
      {...props}
    >
      {children}
    </button>
  );
});

export const ProductField = forwardRef(function ProductField(
  {
    id,
    label,
    hint,
    error,
    success,
    className,
    inputClassName,
    as: Component = 'input',
    ...props
  },
  ref,
) {
  const generatedId = useId();
  const fieldId = id || generatedId;
  const messageId = `${fieldId}-message`;
  const state = error ? 'error' : success ? 'success' : 'default';

  return (
    <div className={cn('linkit-field', className)} data-state={state}>
      {label && <label htmlFor={fieldId}>{label}</label>}
      {React.createElement(Component, {
        ...props,
        ref,
        id: fieldId,
        'aria-invalid': Boolean(error),
        'aria-describedby': (hint || error || success) ? messageId : undefined,
        className: cn('linkit-field-control', inputClassName),
      })}
      {(hint || error || success) && (
        <p id={messageId} className="linkit-field-message">
          {error || success || hint}
        </p>
      )}
    </div>
  );
});

export function ProductSurface({
  children,
  className,
  as: Component = 'section',
  tone = 'default',
  interactive = false,
  ...props
}) {
  return React.createElement(
    Component,
    {
      ...props,
      className: cn(
        'linkit-surface',
        `linkit-surface-${tone}`,
        interactive && 'linkit-surface-interactive',
        className,
      ),
    },
    children,
  );
}

const NOTICE_ICONS = {
  info: Info,
  success: Check,
  warning: AlertTriangle,
  danger: XCircle,
  security: LockKeyhole,
};

export function InlineNotice({
  title,
  description,
  tone = 'info',
  action,
  className,
  role,
}) {
  const Icon = NOTICE_ICONS[tone] || Info;
  return (
    <div
      className={cn('linkit-notice', `linkit-notice-${tone}`, className)}
      role={role || (tone === 'danger' ? 'alert' : 'status')}
    >
      <span className="linkit-notice-icon"><Icon size={18} /></span>
      <div className="linkit-notice-copy">
        <strong>{title}</strong>
        {description && <p>{description}</p>}
      </div>
      {action && <div className="linkit-notice-action">{action}</div>}
    </div>
  );
}

export function SaveStatus({ state = 'idle', label, className }) {
  const content = {
    idle: ['All changes saved', Check],
    saving: ['Saving changes', LoaderCircle],
    saved: ['Changes saved', Check],
    error: ['Changes not saved', AlertTriangle],
    offline: ['Waiting for connection', RotateCcw],
  };
  const [defaultLabel, Icon] = content[state] || content.idle;
  return (
    <span className={cn('linkit-save-status', className)} data-state={state} role="status">
      <Icon size={13} className={state === 'saving' ? 'animate-spin' : ''} />
      {label || defaultLabel}
    </span>
  );
}

export function ProductEmptyState({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  className,
}) {
  return (
    <div className={cn('linkit-empty-state', className)}>
      {icon && <span className="linkit-empty-state-icon">{icon}</span>}
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {(primaryAction || secondaryAction) && (
        <div className="linkit-empty-state-actions">
          {primaryAction}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}

export function ProductSkeleton({ className, ...props }) {
  return <div className={cn('linkit-skeleton', className)} aria-hidden="true" {...props} />;
}

export function ProductDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  tone = 'default',
  closeLabel = 'Close dialog',
  className,
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Dialog.Portal>
        <Dialog.Overlay className="linkit-dialog-overlay" />
        <Dialog.Content className={cn('linkit-dialog', `linkit-dialog-${tone}`, className)}>
          <Dialog.Close asChild>
            <ProductIconButton className="linkit-dialog-close" label={closeLabel}>
              <X size={18} />
            </ProductIconButton>
          </Dialog.Close>
          <div className="linkit-dialog-header">
            <Dialog.Title>{title}</Dialog.Title>
            {description && <Dialog.Description>{description}</Dialog.Description>}
          </div>
          <div className="linkit-dialog-body">{children}</div>
          {footer && <div className="linkit-dialog-footer">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function ProductSheet({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Dialog.Portal>
        <Dialog.Overlay className="linkit-dialog-overlay" />
        <Dialog.Content className="linkit-sheet">
          <div className="linkit-sheet-handle" aria-hidden="true" />
          <Dialog.Close asChild>
            <ProductIconButton className="linkit-dialog-close" label="Close panel">
              <X size={18} />
            </ProductIconButton>
          </Dialog.Close>
          <div className="linkit-dialog-header">
            <Dialog.Title>{title}</Dialog.Title>
            {description && <Dialog.Description>{description}</Dialog.Description>}
          </div>
          <div className="linkit-dialog-body">{children}</div>
          {footer && <div className="linkit-dialog-footer">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function ProductStateTransition({ children, stateKey, className }) {
  return (
    <MotionDiv
      key={stateKey}
      className={className}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={MOTION_TOKENS.feedback}
    >
      {children}
    </MotionDiv>
  );
}
